/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2021, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import { Injectable } from '@angular/core';
import { Logger } from '@core/lib/services';
import { ProxyDataService } from '@app/core/http/proxy-data.service';
import { appConstants } from '@app/common/app-constants';
import { HttpHeaders } from '@angular/common/http';
import { UtilService } from '@app/util/util.service';
import * as _ from 'underscore';
import { MessageService } from '@core/lib/services/message.service';
import * as moment from 'moment';
import { interval, Observable, Subject, BehaviorSubject } from 'rxjs';
import { NotifyService } from './notify.service';
import { TranslateService } from '@ngx-translate/core';
import { CCSFhircast, DaConfig, EvConfig, Fhircast, IntegrationConfig, NGIFhircast } from '../di/data-model/di-fhircast-config';
import { WFMDateTimeFormat } from '@app/di/data-model/date-time-format';
const log = new Logger('AppConfigService');

@Injectable({
  providedIn: 'root'
})
export class AppConfigService {
  private privileges;
  private userName;
  private displayName;
  private userDto;
  private userDefaultDomain = '';
  private appConfig = new Map<string, any>();
  private unsignedWLTimeDepth;
  private radConfigurations;
  private domainMap;
  private lastActiveWorkList;
  private activeWorkList;
  private groupByPatientCurrentContext;
  private irccConfig;
  private takeControlTimeout = {} as any;
  private takeControlAutoAckTimeout = {} as any;
  private wfmDateTimePref = {} as any;
  private dateTimeObserver = new Subject<any>();
  private systemStatusObserver = new BehaviorSubject({});
  private placeholderTranslations = { M: null, D: null, Y: null };
  private nhiConfig = {} as any;
  private reportingEventsEnabled = false;
  private localPatientNameDisplay = false;

  maxStatCount = 99;
  maxTotalCount: any = 500;
  maxFindingsCount = 99;
  maxTotalCountName = 'maxTotalCount';
  DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss.SSS';
  currentSysDateTime = null;
  subCategory = {
    APPLICATION: 'applicationConfiguration',
    AUDIT: 'auditStatus',
    DICOMQR: 'dicomQRConfig',
    SITE: 'siteConfiguration',
    PROPERTIES: 'properties'
  };

  /* DO NOT ADD any dependencies in the constructor, this will leads to circular dependencies.
   * Be cautious when you do that */
  constructor(
    private dataService: ProxyDataService,
    private messageService: MessageService,
    private notifyService: NotifyService,
    private translate: TranslateService,
    private utilService: UtilService
  ) {
    this.broadcastListeners();
    this.getDefaultDateTimeFormat();
  }

  getDefaultDateTimeFormat() {
    this.translate
      .get([
        'default.dateTimeFormat',
        'default.timeDateFormat',
        'default.dateFormat',
        'default.timeFormat',
        'default.monthDayFormat',
        'default.yearFormat',
        'default.inputFormat',
        'default.date.delimiter',
        'default.placeholderFormat',
        'filtering.date.year',
        'filtering.date.day',
        'filtering.date.month'
      ])
      .toPromise()
      .then(translation => {
        this.wfmDateTimePref.dateTimeFormat = translation['default.dateTimeFormat'];
        this.wfmDateTimePref.timeDateFormat = translation['default.timeDateFormat'];
        this.wfmDateTimePref.dateFormat = translation['default.dateFormat'];
        this.wfmDateTimePref.timeFormat = translation['default.timeFormat'];
        this.wfmDateTimePref.monthDayFormat = translation['default.monthDayFormat'];
        this.wfmDateTimePref.yearFormat = translation['default.yearFormat'];
        this.wfmDateTimePref.inputFormat = translation['default.inputFormat'];
        this.wfmDateTimePref.dateDelimiter = translation['default.date.delimiter'];
        this.wfmDateTimePref.delimiter = translation['default.date.delimiter'];
        this.wfmDateTimePref.placeholderFormat = translation['default.placeholderFormat'];
        this.placeholderTranslations = {
          D: translation['filtering.date.day'],
          M: translation['filtering.date.month'],
          Y: translation['filtering.date.year']
        };
        this.setPlaceholderFormat();
      });
  }

  getAppConfiguration() {
    return new Promise((resolve, reject) => {
      this.dataService.httpGet(appConstants.appURLs.allConfigurations).then(
        (data: any) => {
          _.each(data, (config: string, subCategory: string) => {
            // fix for Application not able to login
            // if JSON.parse fails for invalid json strings
            try {
              this.appConfig.set(subCategory, JSON.parse(config));
            } catch (error) {
              log.debug('Error parsing : ' + subCategory + ':' + config + ' : ' + error);
            }
          });
          this.setIRCCConfiguration(this.appConfig.get(appConstants.configSubCategory.APPLICATION)['ircc.enabled']);
          this.setReportingEventConfig(
            this.appConfig.get(appConstants.configSubCategory.APPLICATION)['enableReportingEvents']
          );
          this.setLocalPatientNameDisplayConfig(
            this.appConfig.get(appConstants.configSubCategory.APPLICATION)['LocalPatientNameDisplay']
          );
          resolve(data);
        },
        error => {
          log.error('Error occurred while getting application configurations ' + JSON.stringify(error));
          reject();
        }
      );
    });
  }

  getRadConfiguration(): Promise<void> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache'
      })
    };
    return new Promise((resolve, reject) => {
      this.dataService
        .httpGet(appConstants.appURLs.propertyConfigurations + appConstants.radConfigProperties.join(','), httpOptions)
        .then(
          resp => {
            this.radConfigurations = new Map<any, any>();
            _.each(resp, (value, key) => {
              this.radConfigurations?.set(key, value);
            });
            this.dataService.setSuppressDataServiceWarnings(
              this.getSuppressUnknownDataAccessErrorNotification() === 'false'
            );
            this.setTakeControlTimeout(this.radConfigurations?.get('timeout.takecontrol'));
            this.setTakeControlAutoAckTimeout(this.radConfigurations?.get('auto.ack.timeout.takecontrol'));
            resolve();
          },
          error => {
            log.error(
              `getRadConfiguration - error occurred while getting rad config, ERROR - ${JSON.stringify(error)}`
            );
            reject(error);
          }
        );
    });
  }

  updateRadConfiguration(property: string) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache'
      })
    };
    return this.dataService.httpGet(appConstants.appURLs.propertyConfigurations + property, httpOptions).then(resp => {
      if (this.radConfigurations?.get(property) && resp && resp[property]) {
        this.radConfigurations.set(property, resp[property]);
      }
    });
  }

  getUserDetails(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.dataService.get(appConstants.appURLs.users + this.getUserId() + '/').then(
        (data: any) => {
          if (data.userName) {
            this.setUserId(data.userName);
          }
          this.setDisplayName(data.firstName + ' ' + data.lastName);
          this.setUserDto(data);
          this.setUserDefaultDomain(data.defaultDomain);
          resolve();
        },
        error => {
          this.setDisplayName(this.getUserId());
          log.error('UserController::: Cannot display username data, Error - ' + JSON.stringify(error));
          reject();
        }
      );
    });
  }

  getDomainInfo() {
    const httpOptions = {
      responseType: 'json',
      headers: new HttpHeaders({
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache'
      })
    };
    return new Promise((resolve, reject) => {
      this.dataService.httpGet(appConstants.appURLs.domainInfo, httpOptions).then(
        result => {
          if (result) {
            this.domainMap = _.mapObject(result, val => {
              return val[0];
            });
            resolve(this.domainMap);
          }
        },
        error => {
          log.error(`Error occurred while getting domain info, ERROR - ${JSON.stringify(error)}`);
          reject(error);
        }
      );
    });
  }

  cacheUnsignedWLTimeDepth() {
    this.dataService
      .get(appConstants.appURLs.workListFilter, {
        worklistName: 'reportstate',
        attributeName: 'study_dttm'
      })
      .then((data: any) => {
        this.unsignedWLTimeDepth = data.days;
      })
      .catch(error => {
        log.error(`Error occurred while getting worklist filter, ERROR - ${JSON.stringify(error)}`);
      });
  }

  getAppConfigByKey(key: string) {
    if (this.appConfig.get(appConstants.configSubCategory.APPLICATION)) {
      return this.appConfig.get(appConstants.configSubCategory.APPLICATION)[key];
    }
  }

  getAppConfigBySubCategory(subCategory) {
    return this.appConfig.get(subCategory);
  }

  getUnsignedWLTimeDepth() {
    return this.unsignedWLTimeDepth;
  }

  getUserId() {
    return this.userName;
  }

  setUserId(userName: string) {
    this.userName = userName;
  }

  setDisplayName(name) {
    this.displayName = name;
  }

  getDisplayName() {
    if (!this.displayName) {
      return this.getUserId();
    }
    return this.displayName;
  }

  setUserDefaultDomain(domain) {
    this.userDefaultDomain = domain;
  }

  getUserDefaultDomain() {
    if (!this.userDefaultDomain) {
      return '';
    }
    return this.userDefaultDomain;
  }

  isIRCCEnabled() {
    return (
      this.userHasIRCCPrivilege() && !!this.irccConfig && (this.irccConfig === 'ON' || this.irccConfig === 'SIRCC')
    );
  }

  isSIRCCEnabled() {
    return this.userHasIRCCPrivilege() && !!this.irccConfig && this.irccConfig === 'SIRCC';
  }

  setUserDto(dto) {
    this.userDto = dto;
  }

  getUserDto() {
    return this.userDto;
  }

  getRadDesktopVersion() {
    return this.radConfigurations?.get('raddesktop.version');
  }

  getSessionMaxInactivity() {
    return this.radConfigurations?.get('session.maxInActivity');
  }

  getNgvSimulator() {
    return this.utilService.getSystemProp()?.['ngv.simulator'];
  }

  getExamNotesEnabled() {
    return UtilService.isEnabled(this.radConfigurations?.get('examnotes.enabled'));
  }

  getWorklistRefresh() {
    return UtilService.isEnabled(this.radConfigurations?.get('worklist.refresh.saveReport'));
  }

  getWorklistPageSize(): number {
    return parseInt(this.radConfigurations?.get('worklist.pageSize'), 10);
  }

  getIRCCEmrDomain() {
    return this.radConfigurations?.get('ircc.emr.domain');
  }

  getNonImagingModalites() {
    return this.radConfigurations?.get('non.imaging.modalities');
  }

  getSIRCCTopCount() {
    return this.radConfigurations?.get('sircc.top.count');
  }

  getCopyRightYear() {
    return this.radConfigurations?.get('copyright.year');
  }

  getProductName() {
    return this.radConfigurations?.get('product.name');
  }

  getFleetConfig() {
    return this.radConfigurations?.get('fleet.protocoling');
  }

  getFleetPolaris() {
    return this.radConfigurations?.get('fleet.polaris');
  }

  getHostName() {
    return this.radConfigurations?.get('hostname');
  }

  getDicomSendStatusPollInterval() {
    return this.radConfigurations?.get('dicom.send.status.poll.interval');
  }

  getRemoteSearchMaxResultsCount() {
    return this.radConfigurations?.get('remote.search.max.results.count');
  }

  getSuppressUnknownDataAccessErrorNotification() {
    return this.radConfigurations?.get('suppress.unknownDataAccessError.notification');
  }

  getRisicEnabled() {
    return this.radConfigurations?.get('osgi.risic.enabled');
  }

  getTimeoutEditLockHold() {
    const editLockTimeout = this.getAppConfigByKey('timeout.edit.lock.hold');
    return editLockTimeout ? parseInt(editLockTimeout) : appConstants.TIME.THREE_MINUTES;
  }

  isExamNotesRestricted() {
    return UtilService.isEnabled(this.radConfigurations?.get('examnotes.edit.restriction.enabled'));
  }

  getProtocolEnabled() {
    return (
      this.radConfigurations?.get('protocol.enabled') === 'true' &&
      this.radConfigurations?.get('osgi.risic.enabled') === 'true'
    );
  }

  setTakeControlTimeout(value) {
    if (!isNaN(value) && parseInt(value) >= appConstants.TAKE_CONTROL_CONSTANTS.defaultTimeout) {
      this.takeControlTimeout.seconds = value;
    } else {
      this.takeControlTimeout.seconds = appConstants.TAKE_CONTROL_CONSTANTS.defaultTimeout;
    }
    this.takeControlTimeout.milliseconds = this.takeControlTimeout.seconds * 1000;
  }

  getTakeControlTimeout() {
    return this.takeControlTimeout;
  }

  setTakeControlAutoAckTimeout(value) {
    if (!isNaN(value)) {
      this.takeControlAutoAckTimeout.seconds = value;
      this.takeControlAutoAckTimeout.milliseconds = this.takeControlAutoAckTimeout.seconds * 1000;
    }
  }

  getTakeControlAutoAckTimeout() {
    return this.takeControlAutoAckTimeout;
  }

  getDomainSysCode(authorityName) {
    return this.domainMap[authorityName];
  }

  getDomainMap() {
    return this.domainMap;
  }

  // last-non-search-worklist-state
  setLastActiveWorklistState(value) {
    if (!UtilService.isSearchList(value)) {
      this.lastActiveWorkList = value;
    }
  }

  updateLastActiveWorklist(value) {
    this.lastActiveWorkList = value;
  }

  getLastActiveWorklist() {
    return this.lastActiveWorkList;
  }

  getActiveList(): any {
    return this.activeWorkList ? this.activeWorkList : {};
  }

  setActiveList(list): void {
    this.activeWorkList = list;
  }

  listProperties() {
    return new Promise(resolve => {
      const data = this.getAppConfigBySubCategory(appConstants.configSubCategory.PROPERTIES);
      if (data) {
        this.currentSysDateTime = moment(data['systemDateTime'], this.DATE_FORMAT).format(this.DATE_FORMAT);
        interval(1000).subscribe(() => {
          this.currentSysDateTime = moment(this.currentSysDateTime, this.DATE_FORMAT)
            .add(1, 'seconds')
            .format(this.DATE_FORMAT);
        });
      }
      resolve(data);
    });
  }

  getCurrentSysDateTime() {
    return this.currentSysDateTime;
  }

  updateMaxTotalCount(): Promise<any> {
    return this.listProperties().then(
      (data: any) => {
        if (data) {
          this.maxTotalCount = data[this.maxTotalCountName];
        }
      },
      error => {
        this.notifyService.error(error);
      }
    );
  }

  getGroupByPatientContext() {
    return this.groupByPatientCurrentContext;
  }

  setGroupByPatientContext(context) {
    this.groupByPatientCurrentContext = context;
  }

  getChangePasswordRegX() {
    return new Promise((resolve, reject) => {
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      };
      this.dataService.httpGet(appConstants.appURLs.changePasswordRegx, httpOptions).then(
        result => {
          resolve(result);
        },
        error => {
          log.error('Error occurred while getting change password regex, ERROR - ', JSON.stringify(error));
          reject(error);
        }
      );
    });
  }

  getPasswordExpired(days: string) {
    return new Promise((resolve, reject) => {
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      };
      this.dataService.httpGet(appConstants.appURLs.passwordExpired + days, httpOptions).then(
        result => {
          resolve(result);
        },
        error => {
          log.error('Error occurred while checking for password expiration', JSON.stringify(error));
          reject(error);
        }
      );
    });
  }

  setUserPrivileges(privileges) {
    this.privileges = privileges;
  }

  readNHIConfiguration() {
    this.dataService.get('config/nhiConfiguration').then(
      data => {
        this.nhiConfig = data;
        log.info('Desktop Integration Config - getOrganizationMap - Received : ' + JSON.stringify(this.nhiConfig));
      },
      reason => {
        // error in retrieve nhiConfiguration
        log.error(
          'Desktop Integration Config - readNHIConfiguration() : Error occurred while receiving ' +
            'nhiConfiguration data. Error: ' +
            JSON.stringify(reason)
        );
      }
    );
  }

  isNHIEnabled() {
    return this.nhiConfig?.nhiEnabled ? this.nhiConfig.nhiEnabled : false;
  }

  getNHIType() {
    return this.nhiConfig ? this.nhiConfig.nhiType : '';
  }

  getNHIDomainName() {
    return this.nhiConfig ? this.nhiConfig.nhiDomainName : '';
  }

  getNHIPattern() {
    return this.nhiConfig ? this.nhiConfig.nhiPattern : '';
  }

  private updateActiveWorkListListener(data: any) {
    this.setLastActiveWorklistState(this.activeWorkList);
    this.setActiveList(data.list);
  }

  private broadcastListeners() {
    this.messageService.on('list.display').subscribe((data: any) => {
      this.updateActiveWorkListListener(data);
    });
    this.messageService.on('list.update').subscribe((data: any) => {
      this.updateActiveWorkListListener(data);
    });
  }

  private userHasIRCCPrivilege() {
    return this.privileges && _.contains(this.privileges, 'ID_IRCC');
  }

  public hasUserRemoteRepositoryAccess(remoteRepositoryName: string) {
    return (
      !this.isRemoteRepositoryAccessControlEnabled() ||
      (this.privileges && remoteRepositoryName && _.contains(this.privileges, 'AccessRepo_' + remoteRepositoryName))
    );
  }

  private setIRCCConfiguration(configurationValue) {
    this.irccConfig = configurationValue;

    if (!this.isSIRCCEnabled()) {
      this.messageService.broadcast('removeSIRCCTermsHighlightStyle');
    }
  }

  setReportingEventConfig(configValue) {
    this.reportingEventsEnabled = configValue === 'true';
  }

  setLocalPatientNameDisplayConfig(configValue) {
    this.localPatientNameDisplay = configValue === 'true';
  }

  getLocalPatientNameDisplay() {
    return this.localPatientNameDisplay;
  }

  isReportingEventsEnabled() {
    return this.reportingEventsEnabled;
  }

  getSystemStatusObserver(): Observable<any> {
    return this.systemStatusObserver.asObservable();
  }

  getSystemStatusNotifier() {
    return this.systemStatusObserver;
  }

  getAlwaysActiveTab() {
    return this.radConfigurations?.get('alwaysActiveTab');
  }

  /**
   * Returns the current date-time preferences object.
   * @returns {WFMDateTimeFormat} An object of type `WFMDateTimeFormat`
   */
  getDateTimePref(): WFMDateTimeFormat {
    return this.wfmDateTimePref;
  }

  /**
   * Returns an observable that emits the current date-time preferences.
   * This can be used to subscribe to changes in the date-time format preferences.
   * @returns {Observable<WFMDateTimeFormat>} An observable of type `WFMDateTimeFormat`
   * that emits the current date-time preferences.
   */
  getDateTimeObserver(): Observable<WFMDateTimeFormat> {
    return this.dateTimeObserver.asObservable();
  }

  /**
   * Updates the date-time preferences and broadcasts the changes.
   * @param dateTimePref - The new date-time format preferences of type `WFMDateTimeFormat`.
   */
  setDateTimePref(dateTimePref: WFMDateTimeFormat): void {
    this.systemStatusObserver.next({ key: 'dateTimePref', message: '' });
    dateTimePref.delimiter = dateTimePref.dateDelimiter;
    this.wfmDateTimePref = dateTimePref;
    this.setPlaceholderFormat();
    this.dateTimeObserver.next(this.wfmDateTimePref);
    this.messageService.broadcast('DateTimePrefChanged', this.wfmDateTimePref);
  }

  /**
   * Updates the date-time placeholder of wfmDateTimePref object.
   * Uses placeholderTranslations to replace the date format acronyms (M, D, Y)
   * with their respective translations.
   */
  setPlaceholderFormat() {
    const dateFormat = this.wfmDateTimePref.inputFormat;
    const accronyms = dateFormat.split(this.wfmDateTimePref.dateDelimiter);
    let placeholder = dateFormat;
    accronyms.forEach((element: string) => {
      const key = element[0];
      if (key) {
        placeholder = placeholder.replace(element, this.placeholderTranslations[key]);
      }
    });
    this.wfmDateTimePref.placeholderFormat = placeholder;
  }

  getDigitalSignatureType() {
    return this.getAppConfigByKey(appConstants.DIGITAL_SIGN_CONFS.signatureTypeKey);
  }

  isDigitalSignatureEnabled() {
    return this.getAppConfigByKey(appConstants.DIGITAL_SIGN_CONFS.signatureEnabledKey) === String(true);
  }

  isV8BackEnd() {
    return this.getAppConfigByKey(appConstants.CPACS_CONFIG_KEYS.version) === appConstants.PACS_VERSION8;
  }

  getfhirCastIntegrationConfig(): IntegrationConfig | null {
    const { configSubCategory, FHIRCAST_NGI_KEY, FHIRCAST_DEFAULT_CONFIG } = appConstants;
    const fhircastCCS: CCSFhircast = this.getAppConfigBySubCategory(configSubCategory.FHIRCAST) ?? null;
    let evConfig: EvConfig = this.getAppConfigBySubCategory(configSubCategory.EVCONFIG) ?? null;
    const daConfig: DaConfig = this.getAppConfigBySubCategory(configSubCategory.DACONFIG) ?? null;
    const ngiFhirCastConfigStr: string = this.getAppConfigByKey(FHIRCAST_NGI_KEY);

    if (!fhircastCCS) {
      return null;
    }

    let ngiFhirCastConfig: NGIFhircast = FHIRCAST_DEFAULT_CONFIG;
    try {
      // Parse the NGI Fhircast configuration string if it exists
      // The default configuration will be taken from appConstants,
      // if ngiConfig is available, then it will replace the default values by spread operator
      const ngiFhircastConfigJSON = JSON.parse(ngiFhirCastConfigStr);
      ngiFhirCastConfig = { ...ngiFhirCastConfig, ...ngiFhircastConfigJSON };
    } catch (error) {
      log.error('NGI Config Fhircast Config Failed to parse, Fall back to default', error);
    }

    const fhircastConfig: Fhircast = {
      Url: fhircastCCS.Url,
      SubscribeEndpoint: fhircastCCS.SubscribeEndpoint,
      PublishEndpoint: fhircastCCS.PublishEndpoint,
      HubTopic: UtilService.getUUID(),
      RetryPeriod: ngiFhirCastConfig.RetryPeriod,
      RetryMaxcount: ngiFhirCastConfig.RetryMaxcount,
      ExamRelaunchDelay: ngiFhirCastConfig.ExamRelaunchDelay,
      ImagingStudySource: ngiFhirCastConfig.ImagingStudySource,
      ImagingStudyResource: ngiFhirCastConfig.ImagingStudyResource,
      WebSocketUseSecureProtocol: ngiFhirCastConfig.WebSocketUseSecureProtocol,
      WebSocketProtocolPrefix: ngiFhirCastConfig.WebSocketProtocolPrefix,
      WebSocketProtocolTokenPrefix: ngiFhirCastConfig.WebSocketProtocolTokenPrefix,
      MicroappTaskStatusCheckInterval: ngiFhirCastConfig.MicroappTaskStatusCheckInterval
    };

    if (evConfig) {
      evConfig = {
        ...evConfig,
        EvHealthCheckInterval: ngiFhirCastConfig?.EvHealthCheckInterval,
        EvLaunchRetryMaxCount: ngiFhirCastConfig?.EvLaunchRetryMaxCount,
        EvRequestTimeout: ngiFhirCastConfig?.EvRequestTimeout
      };
    }

    const ngvSimulator = this.getNgvSimulator();
    if (ngvSimulator) {
      fhircastConfig.Url = ngvSimulator;
      evConfig.Url = ngvSimulator;
      fhircastConfig.SubscribeEndpoint = { v1: '/subscription' };
      fhircastConfig.PublishEndpoint = { v1: '/event' };
    }

    return {
      Fhircast: fhircastConfig,
      Ev: evConfig,
      Da: daConfig
    };
  }

  isInstituteSeparationEnabled() {
    return this.getAppConfigByKey(appConstants.ENABLE_INSTITUTE_SEPARATION) === 'true';
  }

  isRemoteRepositoryAccessControlEnabled() {
    return this.getAppConfigByKey(appConstants.ENABLE_REMOTE_REPOSITORY_ACCESS_CONTROL) === 'true';
  }
}
